import { useState, useMemo } from 'react';
import {
  Stack,
  TextInput,
  Select,
  Button,
  Alert,
  Paper,
  Text,
  Code,
  CopyButton,
  Group,
  ActionIcon,
  Loader,
  Grid,
  Box,
} from '@mantine/core';
import { IconCopy, IconCheck, IconMail } from '@tabler/icons-react';
import {
  useGenerateEmailContent,
  type EmailDocumentType,
} from 'src/meeting-note/hooks/useGenerateEmailContent';
import { useClients } from 'src/clients/hooks/useClients';
import { useMeetingNotes } from 'src/meeting-note/hooks/useMeetingNotes';
import { useMeetingAgenda } from 'src/suitability-report/hooks/useMeetingAgendas';
import { ExampleStack, type Example } from '../';

const DOCUMENT_TYPES: { value: EmailDocumentType; label: string }[] = [
  { value: 'MEETING_NOTE', label: 'Meeting Note' },
  { value: 'MEETING_AGENDA', label: 'Meeting Agenda' },
];

const OUTPUT_PANEL_HEIGHT = 320;

function dropdownSelectionDemo(): Example {
  return {
    description: 'Select client and document from dropdowns',
    render: <DropdownSelectionGenerator />,
  };
}

function manualIdDemo(): Example {
  return {
    description: 'Enter client and document IDs manually',
    render: <ManualIdGenerator />,
  };
}

export default function EmailContentSandbox() {
  return <ExampleStack examples={[dropdownSelectionDemo(), manualIdDemo()]} />;
}

function DropdownSelectionGenerator() {
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [documentType, setDocumentType] = useState<EmailDocumentType>('MEETING_NOTE');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const { clients, isLoading: clientsLoading } = useClients();
  const { meetingNotes, isLoading: notesLoading } = useMeetingNotes({
    clientId: selectedClientId || undefined,
  });
  const { meetingAgendas, isLoading: agendasLoading } = useMeetingAgenda({
    clientId: selectedClientId || undefined,
  });

  const { generateEmailContent, isLoading: generating, isError, error } = useGenerateEmailContent();

  const clientOptions = useMemo(
    () =>
      clients.map(client => ({
        value: client.id,
        label: `${client.firstName} ${client.lastName}`.trim() || client.emailAddress || client.id,
      })),
    [clients],
  );

  const documentOptions = useMemo(() => {
    if (documentType === 'MEETING_NOTE') {
      return meetingNotes.map(note => ({
        value: note.id,
        label:
          note.displayName || `Meeting Note (${new Date(note.createdAt).toLocaleDateString()})`,
      }));
    }
    return meetingAgendas.map(agenda => ({
      value: agenda.id,
      label:
        agenda.fileName || `Meeting Agenda (${new Date(agenda.createdAt).toLocaleDateString()})`,
    }));
  }, [documentType, meetingNotes, meetingAgendas]);

  const documentsLoading = documentType === 'MEETING_NOTE' ? notesLoading : agendasLoading;

  const handleClientChange = (clientId: string | null) => {
    setSelectedClientId(clientId);
    setSelectedDocumentId(null);
    setResult(null);
  };

  const handleDocumentTypeChange = (type: EmailDocumentType) => {
    setDocumentType(type);
    setSelectedDocumentId(null);
    setResult(null);
  };

  const handleGenerate = async () => {
    if (!selectedClientId || !selectedDocumentId) return;

    setResult(null);
    const response = await generateEmailContent({
      clientId: selectedClientId,
      documentId: selectedDocumentId,
      documentType,
    });
    if (response?.body) {
      setResult(response.body);
    }
  };

  const isFormValid = selectedClientId && selectedDocumentId;

  return (
    <Grid gutter="lg">
      <Grid.Col span={6}>
        <Stack h={OUTPUT_PANEL_HEIGHT} justify="space-between">
          <Stack gap="sm">
            <Select
              label="Client"
              placeholder={clientsLoading ? 'Loading clients...' : 'Select a client'}
              data={clientOptions}
              value={selectedClientId}
              onChange={handleClientChange}
              searchable
              clearable
              disabled={clientsLoading}
              rightSection={clientsLoading ? <Loader size="xs" /> : undefined}
            />

            <Select
              label="Document Type"
              data={DOCUMENT_TYPES}
              value={documentType}
              onChange={value =>
                handleDocumentTypeChange((value as EmailDocumentType) || 'MEETING_NOTE')
              }
              disabled={!selectedClientId}
            />

            <Select
              label="Document"
              placeholder={
                !selectedClientId
                  ? 'Select a client first'
                  : documentsLoading
                    ? 'Loading documents...'
                    : documentOptions.length === 0
                      ? 'No documents found'
                      : 'Select a document'
              }
              data={documentOptions}
              value={selectedDocumentId}
              onChange={setSelectedDocumentId}
              disabled={!selectedClientId || documentsLoading || documentOptions.length === 0}
              rightSection={documentsLoading ? <Loader size="xs" /> : undefined}
            />
          </Stack>

          <Stack gap="sm">
            {isError && (
              <Alert color="red" title="Error" p="xs">
                {error instanceof Error ? error.message : 'Failed to generate email content'}
              </Alert>
            )}

            <Button
              leftSection={<IconMail size={16} />}
              onClick={handleGenerate}
              loading={generating}
              disabled={!isFormValid}
              fullWidth
            >
              Generate Email Content
            </Button>
          </Stack>
        </Stack>
      </Grid.Col>

      <Grid.Col span={6}>
        <EmailResultPanel result={result} isLoading={generating} />
      </Grid.Col>
    </Grid>
  );
}

function ManualIdGenerator() {
  const [clientId, setClientId] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [documentType, setDocumentType] = useState<EmailDocumentType>('MEETING_NOTE');
  const [result, setResult] = useState<string | null>(null);

  const { generateEmailContent, isLoading, isError, error } = useGenerateEmailContent();

  const handleGenerate = async () => {
    if (!clientId || !documentId) return;

    setResult(null);
    const response = await generateEmailContent({
      clientId,
      documentId,
      documentType,
    });
    if (response?.body) {
      setResult(response.body);
    }
  };

  const isFormValid = clientId.trim() !== '' && documentId.trim() !== '';

  return (
    <Grid gutter="lg">
      <Grid.Col span={6}>
        <Stack h={OUTPUT_PANEL_HEIGHT} justify="space-between">
          <Stack gap="sm">
            <TextInput
              label="Client ID"
              placeholder="e.g., 123e4567-e89b-12d3-a456-426614174000"
              value={clientId}
              onChange={e => setClientId(e.target.value)}
            />

            <TextInput
              label="Document ID"
              placeholder="e.g., 987fcdeb-51a2-43e7-b8c9-012345678901"
              value={documentId}
              onChange={e => setDocumentId(e.target.value)}
            />

            <Select
              label="Document Type"
              data={DOCUMENT_TYPES}
              value={documentType}
              onChange={value => setDocumentType((value as EmailDocumentType) || 'MEETING_NOTE')}
            />
          </Stack>

          <Stack gap="sm">
            {isError && (
              <Alert color="red" title="Error" p="xs">
                {error instanceof Error ? error.message : 'Failed to generate email content'}
              </Alert>
            )}

            <Button
              leftSection={<IconMail size={16} />}
              onClick={handleGenerate}
              loading={isLoading}
              disabled={!isFormValid}
              fullWidth
            >
              Generate Email Content
            </Button>
          </Stack>
        </Stack>
      </Grid.Col>

      <Grid.Col span={6}>
        <EmailResultPanel result={result} isLoading={isLoading} />
      </Grid.Col>
    </Grid>
  );
}

function EmailResultPanel({ result, isLoading }: { result: string | null; isLoading: boolean }) {
  return (
    <Paper
      p="md"
      withBorder
      h={OUTPUT_PANEL_HEIGHT}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <Group justify="space-between" mb="xs">
        <Text fw={500} size="sm">
          Generated Email Body
        </Text>
        {result && (
          <CopyButton value={result}>
            {({ copied, copy }) => (
              <ActionIcon
                color={copied ? 'teal' : 'gray'}
                variant="subtle"
                onClick={copy}
                size="sm"
              >
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            )}
          </CopyButton>
        )}
      </Group>

      <Box style={{ flex: 1, overflow: 'hidden' }}>
        {isLoading ? (
          <Stack align="center" justify="center" h="100%">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Generating...
            </Text>
          </Stack>
        ) : result ? (
          <Code
            block
            style={{
              whiteSpace: 'pre-wrap',
              height: '100%',
              overflowY: 'auto',
              margin: 0,
            }}
          >
            {result}
          </Code>
        ) : (
          <Stack align="center" justify="center" h="100%">
            <Text size="sm" c="dimmed" ta="center">
              Select a document and click generate to see the email content here
            </Text>
          </Stack>
        )}
      </Box>
    </Paper>
  );
}
